import { issueDifficultyValues } from "~/lib/enums"
import { EmptyState } from "~/components/screens/StateScreen"
import { Badge } from "~/components/ui/badge"
import {
	GraduationCapIcon,
	CalendarDaysIcon,
	CheckCircle2Icon,
	ChevronRightIcon,
	LayoutGridIcon,
	ListIcon,
	PlusCircleIcon,
	SlidersHorizontalIcon
} from "lucide-react"
import {
	type ReactNode,
	startTransition,
	useContext,
	useEffect,
	useId,
	useState
} from "react"
import posthog from "posthog-js"
import { posthogEnabled } from "~/components/providers/Posthog"
import { AppHeader } from "~/components/AppHeader"
import { IssueCreateDialog } from "~/components/screens/IssueCreateDialog"
import { LoadingState } from "~/components/screens/StateScreen"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "~/components/ui/button"
import {
	FieldGroup,
	FieldLegend,
	FieldRow,
	FieldSet
} from "~/components/ui/field"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { Separator } from "~/components/ui/separator"
import { Switch } from "~/components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group"
import { Search } from "~/components/ui/search"
import { TutorialContext } from "~/components/tutorials/context"
import { useTeam } from "~/hooks/useTeam"
import { IssuePriority, IssueStatus } from "~/lib/enums"
import { cn, fancyIssueId } from "~/lib/utils"
import type { RouterOutputs } from "~/trpc/react"
import { Kanban } from "../kanban/Kanban"
import { PinnedKBDocuments } from "../knowledgeBase/PinnedKBDocuments"
import { FilterSettings } from "./FilterSettings"
import { IssueCalendar } from "./IssueCalendar"
import IssueList from "./IssueList"
import { IssueOverview } from "./IssueOverview"
import { RefreshButton } from "./RefreshButton"
import { issueRowProperties } from "./IssueRow"
import { Link } from "~/components/Link"

export default function IssuePage({
	teamIssueData,
	customNoIssuesMessage,
	queueMode = false,
	onIssueOpen,
	filters
}: {
	filters?: ReactNode
	teamIssueData: RouterOutputs["issues"]["getTeamIssues"]
	customNoIssuesMessage?: string
	queueMode?: boolean
	onIssueOpen?: (
		issue: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]
	) => void
}) {
	const { team, orgSlug, isLoading: isLoadingTeam } = useTeam()
	const tutorial = useContext(TutorialContext)
	const viewConfigId = useId()
	const [view, setView] = useState<"list" | "kanban" | "calendar">("list")
	const [hideEmptyColumns, setHideEmptyColumns] = useState(false)
	const [groupBy, setGroupBy] = useState<
		"status" | "priority" | "assignee" | "difficulty"
	>("status")
	const [hiddenProperties, setHiddenProperties] = useState<
		(typeof issueRowProperties)[number]["id"][]
	>([])
	const [isCreateOpen, setIsCreateOpen] = useState(false)
	const [issueCreation, setIssueCreation] = useState<{
		groupBy: typeof groupBy
		value: string
	} | null>(null)
	const [scope, setScope] = useState("all")
	const [query, setQuery] = useState("")

	useEffect(() => {
		const storedView = localStorage.getItem(`${team?.id ?? "personal"}-view`)
		setView(
			storedView === "list" ||
				storedView === "kanban" ||
				storedView === "calendar"
				? storedView
				: team?.defaultView === "kanban" || team?.defaultView === "calendar"
					? team.defaultView
					: "list"
		)
		setScope("all")
		setQuery("")
		try {
			const storedProperties = JSON.parse(
				localStorage.getItem(
					`${team?.id ?? "personal"}-hidden-issue-properties`
				) ?? "[]"
			)
			setHiddenProperties(
				issueRowProperties
					.filter(
						({ id }) =>
							Array.isArray(storedProperties) && storedProperties.includes(id)
					)
					.map(({ id }) => id)
			)
		} catch {
			setHiddenProperties([])
		}
	}, [team?.id, team?.defaultView])

	const handleViewChange = (newView: "list" | "kanban" | "calendar") => {
		startTransition(() => {
			setView(newView)
			localStorage.setItem(`${team?.id ?? "personal"}-view`, newView)
		})
	}
	const scopes = [
		{ id: "all", label: "All issues", issues: teamIssueData.issues },
		{
			id: "active",
			label: "Active",
			issues: teamIssueData.issues.filter(
				(issue) =>
					issue.status !== IssueStatus.Complete &&
					issue.status !== IssueStatus.Canceled
			)
		},
		{
			id: "review",
			label: "In review",
			issues: teamIssueData.issues.filter(
				(issue) => issue.status === IssueStatus.InReview
			)
		},
		{
			id: "overdue",
			label: "Overdue",
			issues: teamIssueData.issues.filter(
				(issue) =>
					issue.dueDate &&
					issue.dueDate.getTime() < Date.now() &&
					issue.status !== IssueStatus.Complete &&
					issue.status !== IssueStatus.Canceled
			)
		},
		{
			id: "complete",
			label: "Completed",
			issues: teamIssueData.issues.filter(
				(issue) => issue.status === IssueStatus.Complete
			)
		}
	]
	const scopedIssues =
		scopes.find((item) => item.id === scope)?.issues ?? teamIssueData.issues
	const search = query.trim().toLowerCase()
	const filteredData = {
		...teamIssueData,
		issues: scopedIssues.filter(
			(issue) =>
				!search ||
				[
					issue.title,
					fancyIssueId(issue.friendlyId, issue.team.slug),
					issue.assignee?.orgMember.user.name,
					...issue.labels.map((label) => label.name)
				].some((value) => value?.toLowerCase().includes(search))
		)
	}

	if (isLoadingTeam) return <LoadingState className="bg-transparent" />

	return (
		<main className="flex h-full min-h-0 min-w-0 flex-col">
			<AppHeader
				contentClassName="flex items-center gap-2.5"
				actions={
					<>
						{queueMode && (
							<Button
								variant="ghost"
								size="sm"
								disabled={tutorial.busy}
								onClick={() => tutorial.start("queue-v1")}
							>
								<GraduationCapIcon className="size-4" />
								Tutorial
							</Button>
						)}
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									size="icon"
									aria-label="View config"
									title="View config"
								>
									<SlidersHorizontalIcon className="size-4" />
								</Button>
							</PopoverTrigger>
							<PopoverContent
								align="end"
								size="lg"
								aria-label="View config"
								layout="panel"
							>
								<FieldSet>
									<FieldLegend variant="label">Layout</FieldLegend>
									<ToggleGroup
										type="single"
										value={view}
										onValueChange={(value) => {
											if (
												value === "list" ||
												value === "kanban" ||
												value === "calendar"
											)
												handleViewChange(value)
										}}
										aria-label="Issue layout"
										size="sm"
										spacing={1}
										className="w-full"
									>
										{[
											{ value: "list", label: "List", icon: ListIcon },
											{ value: "kanban", label: "Board", icon: LayoutGridIcon },
											{
												value: "calendar",
												label: "Calendar",
												icon: CalendarDaysIcon
											}
										].map((item) => (
											<ToggleGroupItem
												key={item.value}
												value={item.value}
												className="flex-1"
											>
												<item.icon className="size-4" />
												{item.label}
											</ToggleGroupItem>
										))}
									</ToggleGroup>
								</FieldSet>
								{view === "list" && (
									<>
										<Separator />
										<FieldSet>
											<FieldLegend variant="label">
												Visible properties
											</FieldLegend>
											<FieldGroup data-slot="checkbox-group">
												<FieldRow
													label="Title (always visible)"
													htmlFor={`${viewConfigId}-title`}
												>
													<Switch
														id={`${viewConfigId}-title`}
														checked
														disabled
													/>
												</FieldRow>
												{issueRowProperties.map((property) => (
													<FieldRow
														key={property.id}
														label={property.label}
														htmlFor={`${viewConfigId}-${property.id}`}
													>
														<Switch
															id={`${viewConfigId}-${property.id}`}
															checked={!hiddenProperties.includes(property.id)}
															onCheckedChange={(visible) => {
																const next = visible
																	? hiddenProperties.filter(
																			(id) => id !== property.id
																		)
																	: [...hiddenProperties, property.id]
																setHiddenProperties(next)
																localStorage.setItem(
																	`${team?.id ?? "personal"}-hidden-issue-properties`,
																	JSON.stringify(next)
																)
																if (posthogEnabled)
																	posthog.capture(
																		"issue_view_property_changed",
																		{
																			property: property.id,
																			visible,
																			team_id: team?.id ?? null
																		}
																	)
															}}
														/>
													</FieldRow>
												))}
											</FieldGroup>
										</FieldSet>
									</>
								)}
								{view === "kanban" && (
									<>
										<Separator />
										<FieldRow
											label="Hide empty columns"
											htmlFor={`${viewConfigId}-empty-columns`}
										>
											<Switch
												id={`${viewConfigId}-empty-columns`}
												checked={hideEmptyColumns}
												onCheckedChange={setHideEmptyColumns}
											/>
										</FieldRow>
									</>
								)}
							</PopoverContent>
						</Popover>
						<Search
							ariaLabel="Search issues"
							value={query}
							onChange={setQuery}
							placeholder="Search issues, labels, people…"
						/>
						<IssueOverview teamIssueData={filteredData} />
						<div className="hidden lg:block">
							<RefreshButton />
						</div>
						<div className="lg:hidden">
							<FilterSettings
								mobile
								onChangeGrouping={setGroupBy}
								value={groupBy}
								disableAssigneeGrouping={!team}
								scope={scope}
								onChangeScope={setScope}
								scopes={scopes.map((item) => ({
									id: item.id,
									label: item.label,
									count: item.issues.length
								}))}
								actions={<RefreshButton menuItem />}
							/>
						</div>
						<IssueCreateDialog
							presetTeamId={team?.id}
							open={isCreateOpen}
							onOpenChange={(open) => {
								setIsCreateOpen(open)
								if (!open) setIssueCreation(null)
							}}
							presetValues={{
								status:
									issueCreation?.groupBy === "status"
										? Object.values(IssueStatus).find(
												(value) => value === issueCreation.value
											)
										: undefined,
								priority:
									issueCreation?.groupBy === "priority"
										? Object.values(IssuePriority).find(
												(value) => value === issueCreation.value
											)
										: undefined,
								difficulty:
									issueCreation?.groupBy === "difficulty"
										? [null, ...issueDifficultyValues].find(
												(value) => String(value) === issueCreation.value
											)
										: undefined,
								assigneeId:
									issueCreation?.groupBy === "assignee"
										? issueCreation.value === "unassigned"
											? null
											: issueCreation.value
										: undefined
							}}
						>
							<Button size="sm" aria-label="New issue">
								<PlusCircleIcon className="size-4" />
								<span className="hidden sm:inline">New issue</span>
							</Button>
						</IssueCreateDialog>
					</>
				}
			>
				{team && (
					<>
						<Link
							to={`/${orgSlug}/${team.slug}/${team.enableHome ? "home" : "issues"}`}
							className="hover:underline"
						>
							<EntityIdentity kind="team" name={team.name} image={team.logo} />
						</Link>
						<ChevronRightIcon className="size-3 shrink-0 text-text-small" />
					</>
				)}
				<h1
					data-tutorial={queueMode ? "queue-heading" : undefined}
					className="shrink-0 text-sm font-semibold text-text-main"
				>
					{queueMode ? "Queue" : team ? "Issues" : "My issues"}
				</h1>
				<Badge variant="count" className="hidden sm:inline">
					{teamIssueData.issues.length}
				</Badge>
			</AppHeader>
			<div className="flex shrink-0 flex-col gap-3 px-4 py-4 sm:px-6">
				{filters}
				<div className="hidden min-w-0 items-center justify-between gap-2 md:flex">
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<div
							role="group"
							aria-label="Filter issues by status"
							className="flex min-w-0 flex-1 flex-wrap items-center gap-1"
						>
							{scopes.map((item) => (
								<Button
									key={item.id}
									variant={scope === item.id ? "outline" : "ghost"}
									size="sm"
									aria-pressed={scope === item.id}
									className={cn(
										"shrink-0",
										item.id === "overdue" &&
											item.issues.length > 0 &&
											"text-amber-300"
									)}
									onClick={() => setScope(item.id)}
								>
									{item.label}
									<span className="text-[10px] tabular-nums opacity-65">
										{item.issues.length}
									</span>
								</Button>
							))}
						</div>
						<div className="hidden shrink-0 items-center gap-2 lg:flex">
							<FilterSettings
								onChangeGrouping={setGroupBy}
								value={groupBy}
								disableAssigneeGrouping={!team}
							/>
						</div>
					</div>
				</div>
				{team && (
					<PinnedKBDocuments
						teamId={team.id}
						orgSlug={orgSlug}
						teamSlug={team.slug}
					/>
				)}
			</div>
			<div
				data-tutorial={queueMode ? "queue-issues" : undefined}
				className="min-h-0 min-w-0 flex-1 overflow-hidden"
			>
				{!filteredData.issues.length ? (
					<EmptyState
						icon={<CheckCircle2Icon className="size-6 text-orbit-text" />}
						title={
							teamIssueData.issues.length
								? "No matching issues"
								: "A clear space for your next idea"
						}
						description={
							teamIssueData.issues.length
								? "Try another view or clear your filters."
								: (customNoIssuesMessage ??
									"Create your first issue to get things moving.")
						}
						actions={
							<>
								{" "}
								{teamIssueData.issues.length ? (
									<Button
										variant="outline"
										onClick={() => {
											setScope("all")
											setQuery("")
										}}
									>
										Clear filters
									</Button>
								) : (
									<Button onClick={() => setIsCreateOpen(true)}>
										<PlusCircleIcon className="size-4" />
										New issue
									</Button>
								)}{" "}
							</>
						}
					/>
				) : view === "list" ? (
					<div className="h-full min-w-0 px-3 pb-6 pt-1 sm:px-6">
						<IssueList
							teamIssueData={filteredData}
							groupBy={groupBy}
							hiddenProperties={hiddenProperties}
							onIssueOpen={onIssueOpen}
							onCreateIssue={(value) => {
								setIssueCreation({ groupBy, value })
								setIsCreateOpen(true)
							}}
						/>
					</div>
				) : view === "kanban" ? (
					<Kanban
						teamIssueData={filteredData}
						hideEmptyColumns={hideEmptyColumns}
						onIssueOpen={onIssueOpen}
						groupBy={groupBy}
						onCreateIssue={(value) => {
							setIssueCreation({ groupBy, value })
							setIsCreateOpen(true)
						}}
					/>
				) : (
					<IssueCalendar
						teamIssueData={filteredData}
						onIssueOpen={onIssueOpen}
					/>
				)}
			</div>
		</main>
	)
}
